// Intercompany Transactions page
import React, { useState, useEffect } from 'react';
import {
    Box, Button, Typography, Stack, Paper, Grid, Divider, TextField, MenuItem,
    Dialog, DialogTitle, DialogContent, DialogActions, Chip, IconButton, Tooltip,
    Tabs, Tab,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LinkIcon from '@mui/icons-material/Link';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
    SummaryGrid, SectionHeader, DrillTable, EmptyState, LoadingBlock,
    StatusPill, fmtMoney, fmtDate, CHART_COLORS,
} from '../agents/AgentUIKit';
import {
    listICTxns, createICTxn, postICTxn, matchICTxns, markMatched, listEntities,
} from './api';

const IC_TYPES = ['SERVICE', 'SALE', 'LOAN', 'TRANSFER', 'DIVIDEND', 'ALLOCATION', 'OTHER'];
const IC_STATUSES = ['', 'DRAFT', 'PENDING_APPROVAL', 'POSTED', 'SETTLED', 'VOID'];

export default function Intercompany() {
    const [tab, setTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [txns, setTxns] = useState([]);
    const [entities, setEntities] = useState([]);
    const [matchPairs, setMatchPairs] = useState([]);

    const [filterStatus, setFilterStatus] = useState('');
    const [filterUnmatched, setFilterUnmatched] = useState(false);

    // Dialog
    const [dialog, setDialog] = useState(false);
    const [form, setForm] = useState({
        from_entity_id: '', to_entity_id: '', transaction_type: 'SERVICE',
        transaction_date: new Date().toISOString().slice(0, 10),
        amount_from: '', amount_to: '', fx_rate: '',
        description: '', reference_number: '',
    });

    const run = async (fn) => {
        setLoading(true); setError(null);
        try { return await fn(); }
        catch (e) { setError(e?.response?.data?.message || e.message); }
        finally { setLoading(false); }
    };

    const loadTxns = async () => {
        const params = {};
        if (filterStatus) params.status = filterStatus;
        if (filterUnmatched) params.unmatched = 1;
        const r = await run(() => listICTxns(params));
        if (r?.data?.success) setTxns(r.data.data);
    };

    useEffect(() => {
        listEntities().then(r => r?.data?.success && setEntities(r.data.data)).catch(() => {});
    }, []);

    useEffect(() => { loadTxns(); }, []);  // eslint-disable-line

    const submit = async () => {
        if (!form.from_entity_id || !form.to_entity_id || !form.amount_from) return;
        const body = {
            ...form,
            amount_from: parseFloat(form.amount_from),
            amount_to: parseFloat(form.amount_to || form.amount_from),
        };
        if (form.fx_rate) body.fx_rate = parseFloat(form.fx_rate);
        const r = await run(() => createICTxn(body));
        if (r?.data?.success) {
            setDialog(false);
            setForm({ ...form, amount_from: '', amount_to: '', description: '', reference_number: '' });
            await loadTxns();
        }
    };

    const handlePost = async (id) => {
        const r = await run(() => postICTxn(id));
        if (r?.data?.success) await loadTxns();
    };

    const findMatches = async () => {
        const r = await run(() => matchICTxns({ tolerance_days: 5, tolerance_amount: 0.01 }));
        if (r?.data?.success) setMatchPairs(r.data.data.pairs || []);
    };

    const confirmMatch = async (a, b) => {
        const r = await run(() => markMatched({ txn_a_id: a.id, txn_b_id: b.id }));
        if (r?.data?.success) {
            setMatchPairs(matchPairs.filter(p => p.txn_a.id !== a.id));
            await loadTxns();
        }
    };

    const entityLabel = (id) => {
        const e = entities.find(x => x.id === id);
        return e ? `${e.entity_code}` : `#${id}`;
    };

    const summaryCards = [
        { label: 'Total Txns', value: txns.length, accent: 'primary' },
        { label: 'Pending', value: txns.filter(t => t.status === 'DRAFT' || t.status === 'PENDING_APPROVAL').length, accent: 'warning' },
        { label: 'Unmatched', value: txns.filter(t => !t.is_matched).length, accent: 'info' },
        { label: 'Total Amount', value: fmtMoney(txns.reduce((s, t) => s + (t.amount_from || 0), 0)), accent: 'secondary' },
    ];

    return (
        <Box sx={{ p: 2 }}>
            <Stack spacing={1.5}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography variant="h4" fontWeight={700}>Intercompany Transactions</Typography>
                        <Typography variant="caption" color="text.secondary">
                            Cross-entity transfers, loans, services & dividends — with auto-matching
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                        <IconButton size="small" onClick={loadTxns}><RefreshIcon /></IconButton>
                        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setDialog(true)}>
                            New IC Txn
                        </Button>
                    </Stack>
                </Stack>
                {error && <Typography variant="caption" color="error">{error}</Typography>}

                <SummaryGrid cards={summaryCards} />
                <Divider />

                <Paper variant="outlined" sx={{ p: 0 }}>
                    <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
                        <Tab label="Transactions" />
                        <Tab label="Auto-Match Pairs" />
                    </Tabs>
                    <Box sx={{ p: 1.5 }}>
                        {tab === 0 && (
                            <Stack spacing={1}>
                                <Stack direction="row" spacing={1}>
                                    <TextField size="small" select label="Status" value={filterStatus}
                                        onChange={e => setFilterStatus(e.target.value)} sx={{ minWidth: 180 }}>
                                        {IC_STATUSES.map(s => <MenuItem key={s} value={s}>{s || 'All'}</MenuItem>)}
                                    </TextField>
                                    <Button size="small" variant={filterUnmatched ? 'contained' : 'outlined'}
                                        onClick={() => setFilterUnmatched(!filterUnmatched)}>
                                        {filterUnmatched ? 'Unmatched Only ✓' : 'Show Only Unmatched'}
                                    </Button>
                                    <Button size="small" variant="outlined" onClick={loadTxns}>Apply</Button>
                                </Stack>
                                {loading ? <LoadingBlock rows={4} /> : (
                                    <DrillTable
                                        columns={[
                                            { key: 'transaction_date', label: 'Date', render: r => fmtDate(r.transaction_date) },
                                            { key: 'from_entity_id', label: 'From', render: r => entityLabel(r.from_entity_id) },
                                            { key: 'to_entity_id', label: 'To', render: r => entityLabel(r.to_entity_id) },
                                            { key: 'transaction_type', label: 'Type', render: r => <Chip size="small" label={r.transaction_type} variant="outlined" /> },
                                            { key: 'amount_from', label: 'Amount', align: 'right', render: r => `${fmtMoney(r.amount_from)} ${r.from_currency}` },
                                            { key: 'description', label: 'Description' },
                                            { key: 'is_matched', label: 'Matched', render: r => r.is_matched ? <Chip size="small" color="success" label="Matched" /> : <Chip size="small" label="—" /> },
                                            { key: 'status', label: 'Status', render: r => <StatusPill status={r.status} /> },
                                            { key: 'actions', label: '', render: r => r.status === 'DRAFT' && (
                                                <Tooltip title="Post">
                                                    <IconButton size="small" color="primary" onClick={() => handlePost(r.id)}><PlayArrowIcon fontSize="small" /></IconButton>
                                                </Tooltip>
                                            )},
                                        ]}
                                        rows={txns}
                                        maxHeight={520}
                                    />
                                )}
                            </Stack>
                        )}
                        {tab === 1 && (
                            <Stack spacing={1}>
                                <SectionHeader
                                    title="Auto-Match Candidates"
                                    subtitle="Find matching IC pairs across entities (same amount, opposite direction, close dates)"
                                    actions={<Button size="small" variant="contained" startIcon={<PlayArrowIcon />} onClick={findMatches}>Run Matcher</Button>}
                                />
                                {matchPairs.length === 0 ? (
                                    <EmptyState message="Click Run Matcher to find pairs." />
                                ) : (
                                    <Stack spacing={1}>
                                        {matchPairs.map((p, i) => (
                                            <Paper key={i} variant="outlined" sx={{ p: 1.5, borderLeft: '3px solid', borderColor: 'info.main' }}>
                                                <Grid container spacing={1}>
                                                    <Grid item xs={12} md={5}>
                                                        <Typography variant="caption" fontWeight={600}>Txn A</Typography>
                                                        <Typography variant="body2">
                                                            {entityLabel(p.txn_a.from_entity_id)} → {entityLabel(p.txn_a.to_entity_id)} — {fmtMoney(p.txn_a.amount_from)} ({fmtDate(p.txn_a.transaction_date)})
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={12} md={5}>
                                                        <Typography variant="caption" fontWeight={600}>Txn B</Typography>
                                                        <Typography variant="body2">
                                                            {entityLabel(p.txn_b.from_entity_id)} → {entityLabel(p.txn_b.to_entity_id)} — {fmtMoney(p.txn_b.amount_to)} ({fmtDate(p.txn_b.transaction_date)})
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={12} md={2}>
                                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                                            <Chip size="small" label={`gap: ${p.date_gap_days}d`} variant="outlined" />
                                                            <Button size="small" variant="contained" startIcon={<CheckCircleIcon />}
                                                                onClick={() => confirmMatch(p.txn_a, p.txn_b)}>
                                                                Confirm
                                                            </Button>
                                                        </Stack>
                                                    </Grid>
                                                </Grid>
                                            </Paper>
                                        ))}
                                    </Stack>
                                )}
                            </Stack>
                        )}
                    </Box>
                </Paper>
            </Stack>

            <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>New Intercompany Transaction</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={1} sx={{ pt: 1 }}>
                        <Grid item xs={12} md={6}>
                            <TextField size="small" fullWidth select label="From Entity" value={form.from_entity_id}
                                onChange={e => setForm({ ...form, from_entity_id: e.target.value })}>
                                {entities.map(ent => <MenuItem key={ent.id} value={ent.id}>{ent.entity_code} — {ent.entity_name} ({ent.functional_currency})</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField size="small" fullWidth select label="To Entity" value={form.to_entity_id}
                                onChange={e => setForm({ ...form, to_entity_id: e.target.value })}>
                                {entities.filter(e => e.id !== form.from_entity_id).map(ent => <MenuItem key={ent.id} value={ent.id}>{ent.entity_code} — {ent.entity_name} ({ent.functional_currency})</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={6} md={4}>
                            <TextField size="small" fullWidth select label="Type" value={form.transaction_type}
                                onChange={e => setForm({ ...form, transaction_type: e.target.value })}>
                                {IC_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={6} md={4}>
                            <TextField size="small" fullWidth type="date" label="Date" InputLabelProps={{ shrink: true }}
                                value={form.transaction_date} onChange={e => setForm({ ...form, transaction_date: e.target.value })} />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField size="small" fullWidth label="Reference" value={form.reference_number}
                                onChange={e => setForm({ ...form, reference_number: e.target.value })} />
                        </Grid>
                        <Grid item xs={6} md={4}>
                            <TextField size="small" fullWidth type="number" label="Amount (From)" value={form.amount_from}
                                onChange={e => setForm({ ...form, amount_from: e.target.value })} />
                        </Grid>
                        <Grid item xs={6} md={4}>
                            <TextField size="small" fullWidth type="number" label="Amount (To) — optional" value={form.amount_to}
                                onChange={e => setForm({ ...form, amount_to: e.target.value })} />
                        </Grid>
                        <Grid item xs={6} md={4}>
                            <TextField size="small" fullWidth type="number" label="FX Rate (if cross-currency)" value={form.fx_rate}
                                onChange={e => setForm({ ...form, fx_rate: e.target.value })} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField size="small" fullWidth multiline rows={2} label="Description" value={form.description}
                                onChange={e => setForm({ ...form, description: e.target.value })} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={submit} disabled={!form.from_entity_id || !form.to_entity_id || !form.amount_from || loading}>
                        Create
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
